import {useEffect, useMemo, useState} from 'react';
import {onValue, ref} from 'firebase/database';
import {db} from '../utils/firebaseConfig';
import {useAuth} from '../contexts/AuthContext';
import {getCurrentYearMonth} from '../utils/helpers';
import {UtilityRecord} from '../types/utilityRecord';
import {deriveLandlordNotifications} from '../utils/notificationRules';

interface RoomLite {
    name: string;
}

/**
 * Việc đang chờ chủ nhà xử lý - nguồn cho chuông thông báo trên header.
 *
 * CỐ Ý không có node `notifications` trong DB. Mọi mục ở đây suy ra từ trạng
 * thái thật (tháng điện nước đang chờ duyệt, đăng ký chưa duyệt…), nên không bao
 * giờ lệch với thực tế và cũng không cần ai đi dọn thông báo cũ. Đổi lại, thứ
 * gì không phải "việc đang chờ" thì không xuất hiện - đúng ý đồ.
 *
 * Hook này chỉ lo phần ĐỌC DB; việc suy ra danh sách nằm ở
 * utils/notificationRules.ts vì bộ gửi push cũng dựng đúng danh sách đó.
 */
export const useNotifications = () => {
    const {canManage, isAdmin, role} = useAuth();
    const isLandlord = role === 'admin' || role === 'manager' || role === 'viewer';

    const [rooms, setRooms] = useState<Record<string, RoomLite>>({});
    const [records, setRecords] = useState<Record<string, UtilityRecord | null>>({});
    const [registrations, setRegistrations] = useState<Record<string, any>>({});
    const [handovers, setHandovers] = useState<Record<string, any>>({});
    const [emailChanges, setEmailChanges] = useState<Record<string, any>>({});

    const month = getCurrentYearMonth();

    // Tên phòng lấy từ roomDirectory - node nhẹ (chỉ id + tên), không kéo cả hồ sơ
    useEffect(() => {
        if (!isLandlord) return;
        return onValue(ref(db, 'roomDirectory'), snapshot => {
            setRooms(snapshot.exists() ? snapshot.val() : {});
        }, () => setRooms({}));
    }, [isLandlord]);

    // Chuỗi id phòng - đổi khi thêm/bớt phòng, không đổi khi sửa tên
    const roomIdsKey = Object.keys(rooms).sort().join(',');

    useEffect(() => {
        if (!isLandlord || !roomIdsKey) return;

        const unsubscribes = roomIdsKey.split(',').map(roomId =>
            onValue(ref(db, `utilityRecords/${roomId}/${month}`), snapshot => {
                setRecords(prev => ({...prev, [roomId]: snapshot.exists() ? snapshot.val() : null}));
            }, () => setRecords(prev => ({...prev, [roomId]: null}))),
        );

        return () => unsubscribes.forEach(unsubscribe => unsubscribe());
        // Theo SỐ LƯỢNG phòng chứ không theo `rooms`: node roomDirectory phát
        // lại mỗi lần đổi tên phòng, mà đăng ký lại toàn bộ listener chỉ vì đổi
        // tên là thừa.
    }, [isLandlord, roomIdsKey, month]);

    // Đăng ký của người thuê: chủ nhà (Admin/Quản lý) đọc được và duyệt được -
    // xem database.rules.json. Chỉ xem thì không, nên không hiện chuông.
    useEffect(() => {
        if (!canManage) return;
        return onValue(ref(db, 'tenantRegistrations'), snapshot => {
            setRegistrations(snapshot.exists() ? snapshot.val() : {});
        }, () => setRegistrations({}));
    }, [canManage]);

    useEffect(() => {
        if (!canManage) return;
        return onValue(ref(db, 'handoverRequests'), snapshot => {
            setHandovers(snapshot.exists() ? snapshot.val() : {});
        }, () => setHandovers({}));
    }, [canManage]);

    // Đổi email đăng nhập: duyệt = ghi userRoles, mà chỉ Admin ghi được - nên
    // chỉ Admin đọc và chỉ Admin thấy thông báo này.
    useEffect(() => {
        if (!isAdmin) return;
        return onValue(ref(db, 'emailChangeRequests'), snapshot => {
            setEmailChanges(snapshot.exists() ? snapshot.val() : {});
        }, () => setEmailChanges({}));
    }, [isAdmin]);

    const notifications = useMemo(
        () => deriveLandlordNotifications({rooms, records, registrations, handovers, emailChanges}),
        [records, registrations, handovers, emailChanges, rooms],
    );

    return {notifications, count: notifications.length};
};
